import AIResource, { type ClientEventSummary as ClientEventSummaryType } from 'App/resources/AI';
import { useTranslation } from 'Components/Helpers/translate';
import BemClass from '@upsales/components/Utils/bemClass';
import React, { useEffect, useState } from 'react';
import { makeCancelable } from 'Helpers/promise';
import logError from 'Helpers/logError';
import AIInsights from '../AIInsights';

import './ClientEventSummary.scss';

const ClientEventSummary = ({ clientId }: { clientId: number }) => {
	const classes = new BemClass('ClientEventSummary');
	const { t } = useTranslation();

	const [data, setData] = useState<ClientEventSummaryType | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const cancelablePromise = makeCancelable(AIResource.getClientEventSummary(clientId));
		cancelablePromise.promise
			.then(data => {
				setData(data);
			})
			.catch(e => logError(e, 'Failed to fetch client event summary'))
			.finally(() => setLoading(false));

		return () => {
			cancelablePromise.cancel();
		};
	}, []);

	const insights = [
		data?.relationshipHealth
			? {
					icon: data?.isRelationshipHealthPositive ? ('plus' as const) : ('minus' as const),
					color: data?.isRelationshipHealthPositive ? ('green' as const) : ('red' as const),
					text: data?.relationshipHealth ?? '',
					title: t('account.clientEventSummary.relationshipHealth')
			  }
			: null,
		data?.keyProjectContext
			? {
					icon: 'key' as const,
					color: 'blue' as const,
					text: data?.keyProjectContext ?? '',
					title: t('account.clientEventSummary.keyProjectContext')
			  }
			: null,

		data?.recentMilestonesAndActions
			? {
					icon: 'project' as const,
					color: 'turcose' as const,
					text: data?.recentMilestonesAndActions ?? '',
					title: t('account.clientEventSummary.recentMilestonesAndActions')
			  }
			: null,
		data?.recommendedNextSteps
			? {
					icon: 'star' as const,
					color: 'purple' as const,
					text: data?.recommendedNextSteps ?? '',
					title: t('account.clientEventSummary.recommendedNextSteps')
			  }
			: null
	].filter(a => a !== null);

	if (insights.length === 0) {
		return null;
	}

	return (
		<div className={classes.b()}>
			<AIInsights
				type="clientEventSummary"
				isLoading={loading}
				insights={insights}
				title={t('account.aiSummary.title')}
			/>
		</div>
	);
};

export default ClientEventSummary;
